import React, { useState } from 'react';
import {
  Download,
  Upload,
  Trash2,
  Loader2,
  AlertCircle,
  RefreshCw,
  Lock,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Link,
  CheckCircle2,
  XCircle,
  Info,
  X,
} from 'lucide-react';
import { OdbcDriversCoreProps, OdbcCatalogDriver } from '../widget/types';
import { useWidgetTheme, mainAppTheme } from '../widget/useWidgetTheme';

// ============================================
// TYPES
// ============================================

type SortField = 'driver_name' | 'category' | 'version' | 'size_mb' | 'status';
type SortDirection = 'asc' | 'desc';

const CATEGORY_LABELS: Record<string, string> = {
  data_warehouse: 'Data Warehouse',
  cloud_database: 'Cloud Database',
  saas_connector: 'SaaS Connector',
  query_engine: 'Query Engine',
  nosql: 'NoSQL',
};

const INSTALL_TYPE_EXTENSIONS: Record<string, { ext: string; label: string }> = {
  deb: { ext: '.deb', label: 'Debian package (.deb)' },
  rpm: { ext: '.rpm', label: 'RPM package (.rpm)' },
  tar: { ext: '.tar.gz', label: 'Tar archive (.tar.gz)' },
  zip: { ext: '.zip', label: 'Zip archive (.zip)' },
};

// ============================================
// STATUS HELPERS
// ============================================

const getDriverStatus = (
  driver: OdbcCatalogDriver,
  activeJobDriverKey: string | null,
  activeJobStatus: string | null,
  actionLoading: string | null,
): { label: string; color: string; busy: boolean } => {
  const isJobActive =
    activeJobDriverKey === driver.driver_key &&
    (activeJobStatus === 'pending' || activeJobStatus === 'processing');
  const isActionLoading = actionLoading === driver.driver_key;

  if (isJobActive || isActionLoading || driver.install_status === 'installing') {
    return { label: 'Installing…', color: 'var(--v2-warning)', busy: true };
  }
  if (driver.install_status === 'failed') {
    return { label: 'Failed', color: 'var(--v2-error)', busy: false };
  }
  if (driver.is_installed) {
    return { label: 'Installed', color: 'var(--v2-success)', busy: false };
  }
  return { label: 'Not Installed', color: 'var(--v2-text-muted)', busy: false };
};

// ============================================
// URL VALIDATION
// ============================================

interface UrlValidation {
  valid: boolean;
  message: string;
  checks: { label: string; passed: boolean | null }[];
}

const validateDriverUrl = (url: string, installType: string): UrlValidation => {
  const checks: { label: string; passed: boolean | null }[] = [];

  if (!url.trim()) {
    return { valid: false, message: '', checks: [] };
  }

  let parsed: URL | null = null;
  try {
    parsed = new URL(url);
    checks.push({ label: 'Valid URL format', passed: true });
  } catch {
    checks.push({ label: 'Valid URL format', passed: false });
    return { valid: false, message: 'Enter a valid URL', checks };
  }

  const isHttps = parsed.protocol === 'https:';
  checks.push({ label: 'Uses HTTPS', passed: isHttps });

  const expected = INSTALL_TYPE_EXTENSIONS[installType];
  if (expected) {
    const pathLower = parsed.pathname.toLowerCase();
    const extMatch = pathLower.endsWith(expected.ext);
    checks.push({ label: `File type: ${expected.label}`, passed: extMatch });

    if (!isHttps || !extMatch) {
      return {
        valid: false,
        message: !isHttps ? 'URL must use HTTPS' : `URL must point to a ${expected.label} file`,
        checks,
      };
    }
  }

  const hasHost = parsed.hostname.includes('.');
  checks.push({ label: 'Valid download host', passed: hasHost });

  if (!hasHost) {
    return { valid: false, message: 'URL must point to a valid host', checks };
  }

  return { valid: true, message: 'URL looks good', checks };
};

// ============================================
// TIER 2 INSTALL MODAL
// ============================================

const Tier2InstallModal: React.FC<{
  driver: OdbcCatalogDriver;
  onInstall: (driverKey: string, downloadUrl: string) => void;
  onClose: () => void;
  busy: boolean;
  errorMessage: string | null;
}> = ({ driver, onInstall, onClose, busy, errorMessage }) => {
  const [url, setUrl] = useState('');
  const [inputFocused, setInputFocused] = useState(false);

  const validation = validateDriverUrl(url, driver.install_type);
  const expected = INSTALL_TYPE_EXTENSIONS[driver.install_type];

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        animation: 'fadeIn 0.15s ease',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          backgroundColor: 'var(--v2-surface)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--v2-border)',
          boxShadow: 'var(--shadow-lg)',
          width: '520px',
          maxWidth: '90vw',
          animation: 'slideDown 0.15s ease',
        }}
      >
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid var(--v2-border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 'var(--text-lg)', fontWeight: 600, color: 'var(--v2-text)', lineHeight: 'var(--leading-tight)' }}>
              Install {driver.driver_name}
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: 'var(--text-base)', color: 'var(--v2-text-tertiary)', lineHeight: 'var(--leading-normal)' }}>
              Provide the vendor download URL for this driver
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', padding: '4px', cursor: 'pointer',
              color: 'var(--v2-text-muted)', display: 'flex',
              minHeight: 'var(--target-min)', minWidth: 'var(--target-min)',
              alignItems: 'center', justifyContent: 'center',
            }}
          >
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '24px' }}>
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: '10px',
            padding: '14px 16px',
            backgroundColor: 'var(--v2-info-bg)', border: '1px solid #93c5fd',
            borderRadius: 'var(--radius-md)', marginBottom: '20px',
          }}>
            <Info size={16} strokeWidth={1.5} color="var(--v2-primary)" style={{ flexShrink: 0, marginTop: '2px' }} />
            <div style={{ fontSize: 'var(--text-base)', color: 'var(--v2-text-secondary)', lineHeight: 'var(--leading-relaxed)' }}>
              <p style={{ margin: '0 0 8px' }}>
                This driver requires you to download the package directly from the vendor.
                Log in to the vendor portal, copy the download URL, and paste it below.
              </p>
              <p style={{ margin: 0 }}>
                <strong>Expected format:</strong> {expected?.label || driver.install_type}
              </p>
              {driver.vendor_url && (
                <a
                  href={driver.vendor_url} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '8px', color: 'var(--v2-primary)', fontSize: 'var(--text-base)', textDecoration: 'none' }}
                  onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                  onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                >
                  <ExternalLink size={14} /> Go to {driver.driver_name} download page
                </a>
              )}
            </div>
          </div>

          <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--v2-text-secondary)', marginBottom: '6px' }}>
            Download URL
          </label>
          <div style={{ position: 'relative' }}>
            <Link size={16} strokeWidth={1.5} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--v2-text-muted)' }} />
            <input
              type="url" value={url}
              onChange={(e) => setUrl(e.target.value)}
              onFocus={() => setInputFocused(true)}
              onBlur={() => setInputFocused(false)}
              placeholder={`https://vendor.com/downloads/driver${expected?.ext || ''}`}
              style={{
                width: '100%', padding: '10px 14px 10px 40px',
                fontSize: 'var(--text-base)', color: 'var(--v2-text)',
                backgroundColor: 'var(--v2-surface)',
                border: `1px solid ${inputFocused ? 'var(--v2-primary)' : 'var(--v2-input-border)'}`,
                borderRadius: 'var(--radius-md)', outline: 'none',
                transition: 'all 0.15s ease',
                boxShadow: inputFocused ? '0 0 0 3px var(--v2-primary-muted)' : 'none',
                lineHeight: 'var(--leading-normal)',
              }}
            />
          </div>

          {url.trim() && (
            <div style={{ marginTop: '14px' }}>
              {validation.checks.map((check, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0', fontSize: 'var(--text-sm)', color: check.passed ? 'var(--v2-success-dark)' : 'var(--v2-error)', lineHeight: 'var(--leading-normal)' }}>
                  {check.passed ? <CheckCircle2 size={14} strokeWidth={2} /> : <XCircle size={14} strokeWidth={2} />}
                  {check.label}
                </div>
              ))}
            </div>
          )}
        </div>

{errorMessage && (
          <div style={{
            margin: '0 24px 16px', display: 'flex', alignItems: 'flex-start', gap: '10px',
            padding: '12px 14px',
            backgroundColor: 'var(--v2-error-bg)', border: '1px solid #fca5a5',
            borderRadius: 'var(--radius-md)',
          }}>
            <AlertCircle size={16} strokeWidth={1.5} color="var(--v2-error)" style={{ flexShrink: 0, marginTop: '1px' }} />
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--v2-error)', lineHeight: 'var(--leading-relaxed)' }}>
              {errorMessage}
            </span>
          </div>
        )}

        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--v2-border)', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--v2-text-secondary)', backgroundColor: 'transparent', border: '1px solid var(--v2-border)', borderRadius: 'var(--radius-md)', cursor: 'pointer', minHeight: 'var(--target-md)', lineHeight: 'var(--leading-normal)' }}>
            Cancel
          </button>
          <button
            onClick={() => validation.valid && onInstall(driver.driver_key, url.trim())}
            disabled={!validation.valid || busy}
            style={{ padding: '8px 20px', fontSize: 'var(--text-sm)', fontWeight: 600, color: '#fff', backgroundColor: (!validation.valid || busy) ? 'var(--v2-disabled-text)' : 'var(--v2-primary)', border: 'none', borderRadius: 'var(--radius-md)', cursor: (!validation.valid || busy) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px', minHeight: 'var(--target-md)', lineHeight: 'var(--leading-normal)', transition: 'background-color 0.15s ease' }}
          >
            {busy ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Installing…</> : <><Upload size={14} /> Install Driver</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// UNINSTALL CONFIRM MODAL
// ============================================

const UninstallModal: React.FC<{
  driver: OdbcCatalogDriver;
  onUninstall: (key: string) => void;
  onClose: () => void;
  busy: boolean;
  errorMessage: string | null;
}> = ({ driver, onUninstall, onClose, busy, errorMessage }) => {
  const [confirmText, setConfirmText] = useState('');
  const confirmed = confirmText === 'delete';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        animation: 'fadeIn 0.15s ease',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          backgroundColor: 'var(--v2-surface)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid #fca5a5',
          width: '480px',
          maxWidth: '90vw',
          boxShadow: 'var(--shadow-lg)',
          animation: 'slideDown 0.15s ease',
          overflow: 'hidden',
        }}
      >
        {/* Red header */}
        <div style={{
          padding: '16px 24px',
          backgroundColor: 'var(--v2-error-bg)',
          borderBottom: '1px solid #fca5a5',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}>
          <AlertCircle size={18} strokeWidth={2} style={{ color: 'var(--v2-error)', flexShrink: 0 }} />
          <h3 style={{ margin: 0, fontSize: 'var(--text-lg)', fontWeight: 600, color: 'var(--v2-error)', lineHeight: 'var(--leading-tight)' }}>
            Uninstall {driver.driver_name}
          </h3>
        </div>

        <div style={{ padding: '20px 24px 24px' }}>
          {/* Consequences */}
          <div style={{
            padding: '12px 14px',
            backgroundColor: 'var(--v2-error-bg)',
            border: '1px solid #fca5a5',
            borderRadius: 'var(--radius-md)',
            marginBottom: '20px',
          }}>
            <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--v2-error)', marginBottom: '6px' }}>
              This will permanently:
            </div>
            <ul style={{ margin: 0, paddingLeft: '18px', fontSize: 'var(--text-sm)', color: 'var(--v2-error)', lineHeight: 'var(--leading-relaxed)' }}>
              <li>Remove the <strong>{driver.driver_name}</strong> driver from your server</li>
              <li>Break all ODBC connections that use this driver</li>
              <li>Break any Database Apps in your workspace relying on those connections</li>
            </ul>
          </div>

          {/* Type to confirm */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: 'var(--text-sm)',
              fontWeight: 500,
              color: 'var(--v2-text-secondary)',
              marginBottom: '6px',
              lineHeight: 'var(--leading-normal)',
            }}>
              Type <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--v2-text)' }}>delete</strong> to confirm
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              placeholder="delete"
              autoFocus
              onKeyDown={e => { if (e.key === 'Enter' && confirmed) onUninstall(driver.driver_key); }}
              style={{
                width: '100%',
                padding: '9px 12px',
                fontSize: 'var(--text-base)',
                fontFamily: 'var(--font-mono)',
                border: `1px solid ${confirmed ? 'var(--v2-error)' : 'var(--v2-input-border)'}`,
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--v2-surface)',
                color: 'var(--v2-text)',
                boxSizing: 'border-box',
                lineHeight: 'var(--leading-normal)',
                outline: 'none',
              }}
            />
          </div>

{errorMessage && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: '10px',
              padding: '12px 14px', marginBottom: '16px',
              backgroundColor: 'var(--v2-error-bg)', border: '1px solid #fca5a5',
              borderRadius: 'var(--radius-md)',
            }}>
              <AlertCircle size={16} strokeWidth={1.5} color="var(--v2-error)" style={{ flexShrink: 0, marginTop: '1px' }} />
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--v2-error)', lineHeight: 'var(--leading-relaxed)' }}>
                {errorMessage}
              </span>
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button
              onClick={onClose}
              disabled={busy}
              style={{ padding: '8px 16px', fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--v2-text-secondary)', backgroundColor: 'transparent', border: '1px solid var(--v2-border)', borderRadius: 'var(--radius-md)', cursor: 'pointer', minHeight: 'var(--target-md)', lineHeight: 'var(--leading-normal)' }}
            >
              Cancel
            </button>
            <button
              onClick={() => onUninstall(driver.driver_key)}
              disabled={busy || !confirmed}
              style={{
                padding: '8px 20px', fontSize: 'var(--text-sm)', fontWeight: 600, color: '#fff',
                backgroundColor: (busy || !confirmed) ? 'var(--v2-disabled-text)' : 'var(--v2-error)',
                border: 'none', borderRadius: 'var(--radius-md)',
                cursor: (busy || !confirmed) ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', gap: '6px',
                minHeight: 'var(--target-md)', lineHeight: 'var(--leading-normal)',
                transition: 'background-color 0.15s ease',
              }}
            >
              {busy && <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />}
              Uninstall Driver
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// SORTABLE HEADER
// ============================================

const SortableHeader: React.FC<{
  field: SortField;
  label: string;
  sortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
  width?: string;
}> = ({ field, label, sortField: activeField, sortDirection, onSort, width }) => {
  const [hovered, setHovered] = useState(false);
  const isActive = activeField === field;

  return (
    <th
      onClick={() => onSort(field)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '14px 20px', fontSize: 'var(--text-xs)', fontWeight: 500,
        color: isActive ? 'var(--v2-text)' : 'var(--v2-text-muted)',
        textAlign: 'left', borderBottom: '1px solid var(--v2-border)',
        backgroundColor: 'var(--v2-surface-alt)', cursor: 'pointer',
        userSelect: 'none', transition: 'color 0.15s ease', width,
        lineHeight: 'var(--leading-normal)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <span>{label}</span>
        <span style={{ display: 'flex', flexDirection: 'column', opacity: isActive ? 1 : hovered ? 0.5 : 0, transition: 'opacity 0.15s ease' }}>
          {isActive ? (sortDirection === 'asc' ? <ChevronUp size={14} strokeWidth={2} /> : <ChevronDown size={14} strokeWidth={2} />) : <ChevronUp size={14} strokeWidth={1.5} />}
        </span>
      </div>
    </th>
  );
};

// ============================================
// STATUS DOT
// ============================================

const StatusDot: React.FC<{ label: string; color: string }> = ({ label, color }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: color, flexShrink: 0, animation: color === 'var(--v2-warning)' ? 'pulse 1.5s ease-in-out infinite' : 'none' }} />
    <span style={{ fontSize: 'var(--text-base)', color: 'var(--v2-text-secondary)' }}>{label}</span>
  </div>
);

// ============================================
// DRIVER ACTION BUTTON
// ============================================

const DriverAction: React.FC<{
  driver: OdbcCatalogDriver;
  status: { label: string; color: string; busy: boolean };
  tier: number;
  tier2Locked: boolean;
  tier1Full: boolean;
  onInstall: (key: string) => void;
  onOpenTier2Modal: (driver: OdbcCatalogDriver) => void;
  onOpenUninstallModal: (driver: OdbcCatalogDriver) => void;
}> = ({ driver, status, tier, tier2Locked, tier1Full, onInstall, onOpenTier2Modal, onOpenUninstallModal }) => {
  const [hovered, setHovered] = useState(false);

  const baseBtn: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    padding: '6px 14px', fontSize: 'var(--text-sm)', fontWeight: 500,
    borderRadius: 'var(--radius-sm)', cursor: 'pointer',
    transition: 'all 0.15s ease', minHeight: 'var(--target-min)',
    lineHeight: 'var(--leading-normal)',
  };

  if (status.busy) {
    return (
      <span style={{ ...baseBtn, color: 'var(--v2-warning-dark)', backgroundColor: 'var(--v2-warning-bg)', border: '1px solid var(--v2-warning)', cursor: 'default' }}>
        <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Installing…
      </span>
    );
  }

  if (driver.install_status === 'failed' && !driver.is_installed) {
    return (
      <button
        onClick={() => tier === 2 ? onOpenTier2Modal(driver) : onInstall(driver.driver_key)}
        onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
        style={{ ...baseBtn, color: 'var(--v2-error)', backgroundColor: hovered ? 'var(--v2-error-bg)' : 'transparent', border: '1px solid var(--v2-error)' }}
      >
        <RefreshCw size={14} /> Retry
      </button>
    );
  }

  if (driver.is_installed) {
    return (
      <button
        onClick={() => onOpenUninstallModal(driver)}
        onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
        style={{ ...baseBtn, color: hovered ? 'var(--v2-error)' : 'var(--v2-text-tertiary)', backgroundColor: hovered ? 'var(--v2-error-bg)' : 'transparent', border: `1px solid ${hovered ? 'var(--v2-error)' : 'var(--v2-border)'}` }}
      >
        <Trash2 size={14} /> Uninstall
      </button>
    );
  }

  if (tier2Locked) {
    return (
      <span style={{ ...baseBtn, color: 'var(--v2-disabled-text)', backgroundColor: 'var(--v2-disabled-bg)', border: '1px solid var(--v2-border)', cursor: 'not-allowed' }}>
        <Lock size={14} /> Upgrade Plan
      </span>
    );
  }

  if (tier1Full) {
    return (
      <span style={{ ...baseBtn, color: 'var(--v2-disabled-text)', backgroundColor: 'var(--v2-disabled-bg)', border: '1px solid var(--v2-border)', cursor: 'not-allowed' }}>
        <Lock size={14} /> Limit Reached
      </span>
    );
  }

  return (
    <button
      onClick={() => tier === 2 ? onOpenTier2Modal(driver) : onInstall(driver.driver_key)}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ ...baseBtn, color: '#fff', backgroundColor: hovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)', border: 'none' }}
    >
      {tier === 1 ? <Download size={14} /> : <Upload size={14} />} Install
    </button>
  );
};

// ============================================
// DRIVER TABLE
// ============================================

const DriverTable: React.FC<{
  drivers: OdbcCatalogDriver[];
  tier: number;
  tierLimit: { current: number; limit: number; unlimited: boolean };
  canTier2: boolean;
  activeJobDriverKey: string | null;
  activeJobStatus: string | null;
  actionLoading: string | null;
  onInstall: (key: string) => void;
  onOpenTier2Modal: (driver: OdbcCatalogDriver) => void;
  onOpenUninstallModal: (driver: OdbcCatalogDriver) => void;
}> = ({
  drivers, tier, tierLimit, canTier2,
  activeJobDriverKey, activeJobStatus, actionLoading,
  onInstall, onOpenTier2Modal, onOpenUninstallModal,
}) => {
  const [sortField, setSortField] = useState<SortField>('driver_name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDirection('asc'); }
  };

  const getSortValue = (driver: OdbcCatalogDriver, field: SortField): string | number => {
    switch (field) {
      case 'driver_name': return driver.driver_name.toLowerCase();
      case 'category': return (CATEGORY_LABELS[driver.category] || driver.category).toLowerCase();
      case 'version': return driver.version || '';
      case 'size_mb': return driver.size_mb || 0;
      case 'status': return driver.is_installed ? 0 : driver.install_status === 'failed' ? 1 : 2;
      default: return '';
    }
  };

  const sorted = [...drivers].sort((a, b) => {
    const aVal = getSortValue(a, sortField);
    const bVal = getSortValue(b, sortField);
    if (typeof aVal === 'number' && typeof bVal === 'number') return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    return sortDirection === 'asc' ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal));
  });

  const canInstallTier1 = tierLimit.unlimited || tierLimit.current < tierLimit.limit;

  return (
    <div style={{ backgroundColor: 'var(--v2-surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--v2-border)', overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <SortableHeader field="driver_name" label="Driver" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
            <SortableHeader field="category" label="Category" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} width="160px" />
            <SortableHeader field="version" label="Version" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} width="120px" />
            <SortableHeader field="size_mb" label="Size" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} width="90px" />
            <SortableHeader field="status" label="Status" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} width="140px" />
            <th style={{ width: '160px', padding: '14px 20px', backgroundColor: 'var(--v2-surface-alt)', borderBottom: '1px solid var(--v2-border)' }} />
          </tr>
        </thead>
        <tbody>
          {sorted.map((driver) => {
            const status = getDriverStatus(driver, activeJobDriverKey, activeJobStatus, actionLoading);
            const isHovered = hoveredRow === driver.driver_key;
            const tier2Locked = driver.tier === 2 && !canTier2;
            const tier1Full = driver.tier === 1 && !canInstallTier1 && !driver.is_installed;

            const tdStyle: React.CSSProperties = {
              padding: '16px 20px', borderBottom: '1px solid var(--v2-border-light)',
              verticalAlign: 'middle', fontSize: 'var(--text-base)', lineHeight: 'var(--leading-normal)',
            };

            return (
              <tr
                key={driver.driver_key}
                onMouseEnter={() => setHoveredRow(driver.driver_key)}
                onMouseLeave={() => setHoveredRow(null)}
                style={{ backgroundColor: isHovered ? 'var(--v2-surface-hover)' : 'var(--v2-surface)', transition: 'background-color 0.1s ease' }}
              >
                <td style={tdStyle}>
                  <div style={{ fontWeight: 500, color: 'var(--v2-text)', marginBottom: '2px' }}>{driver.driver_name}</div>
                  {driver.install_notes && !driver.is_installed && (
                    <div style={{ fontSize: 'var(--text-sm)', color: 'var(--v2-text-tertiary)', lineHeight: 'var(--leading-normal)' }}>{driver.install_notes}</div>
                  )}
                  {tier === 2 && driver.vendor_url && !driver.is_installed && (
                    <a href={driver.vendor_url} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: 'var(--text-sm)', color: 'var(--v2-primary)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}
                      onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                      onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                    >
                      <ExternalLink size={12} /> Download from vendor
                    </a>
                  )}
                </td>
                <td style={tdStyle}><span style={{ color: 'var(--v2-text-secondary)' }}>{CATEGORY_LABELS[driver.category] || driver.category}</span></td>
                <td style={tdStyle}><span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)', color: 'var(--v2-text-tertiary)' }}>{driver.version || '—'}</span></td>
                <td style={tdStyle}><span style={{ color: 'var(--v2-text-tertiary)' }}>{driver.size_mb ? `${driver.size_mb} MB` : '—'}</span></td>
                <td style={tdStyle}><StatusDot label={status.label} color={status.color} /></td>
                <td style={{ ...tdStyle, textAlign: 'right', paddingRight: '16px' }}>
                  <DriverAction
                    driver={driver} status={status} tier={tier}
                    tier2Locked={tier2Locked} tier1Full={tier1Full}
                    onInstall={onInstall}
                    onOpenTier2Modal={onOpenTier2Modal}
                    onOpenUninstallModal={onOpenUninstallModal}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

const OdbcDriversCore: React.FC<OdbcDriversCoreProps> = ({
  catalog, tierLimit, canTier2, loading, error,
  activeJobDriverKey, activeJobStatus, mode, theme: themeOverrides,
  onInstallDriver, onUninstallDriver, onRefresh, onSuccess, onError,
}) => {
const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [refreshHover, setRefreshHover] = useState(false);
  const [tier2ModalDriver, setTier2ModalDriver] = useState<OdbcCatalogDriver | null>(null);
  const [uninstallModalDriver, setUninstallModalDriver] = useState<OdbcCatalogDriver | null>(null);
  const [installError, setInstallError] = useState<string | null>(null);
  const [uninstallError, setUninstallError] = useState<string | null>(null);

  const tier1Drivers = catalog.filter((d) => d.tier === 1);
  const tier2Drivers = catalog.filter((d) => d.tier === 2);
  const installedCount = catalog.filter((d) => d.is_installed).length;
  const tier1Installed = tier1Drivers.filter((d) => d.is_installed).length;
  const canInstallTier1 = tierLimit.unlimited || tierLimit.current < tierLimit.limit;

const handleInstall = async (driverKey: string, downloadUrl?: string) => {
    setActionLoading(driverKey);
    setInstallError(null);
    try {
      await onInstallDriver(driverKey, downloadUrl);
      // Success path: close modal (if it was open) and let parent's toast fire
      setTier2ModalDriver(null);
    } catch (err: any) {
      // Error path: keep modal open, show inline error, preserve user input
      setInstallError(err.message || 'Failed to install driver');
    } finally {
      setActionLoading(null);
    }
  };

const handleUninstall = async (driverKey: string) => {
    setActionLoading(driverKey);
    setUninstallError(null);
    try {
      await onUninstallDriver(driverKey);
      setUninstallModalDriver(null);
    } catch (err: any) {
      // Error path: keep modal open, show inline error (e.g. "Delete connections first.")
      setUninstallError(err.message || 'Failed to uninstall driver');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '80px 0' }}>
        <div style={{ width: '32px', height: '32px', border: '2px solid var(--v2-border)', borderTopColor: 'var(--v2-primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', backgroundColor: 'var(--v2-error-bg)', border: '1px solid #fca5a5', borderRadius: 'var(--radius-lg)', padding: '16px 20px' }}>
        <AlertCircle size={18} strokeWidth={1.5} color="var(--v2-error)" style={{ flexShrink: 0, marginTop: '1px' }} />
        <div>
          <p style={{ fontSize: 'var(--text-base)', fontWeight: 500, color: 'var(--v2-error)', margin: 0 }}>{error}</p>
          <button onClick={() => onRefresh()} style={{ marginTop: '8px', padding: '6px 12px', fontSize: 'var(--text-sm)', backgroundColor: 'var(--v2-surface)', border: '1px solid #fca5a5', borderRadius: 'var(--radius-sm)', cursor: 'pointer', color: 'var(--v2-error)' }}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: 'var(--font-sans)' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 'var(--text-xl)', fontWeight: 600, color: 'var(--v2-text)', lineHeight: 'var(--leading-tight)' }}>
            ODBC Drivers
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: 'var(--text-base)', color: 'var(--v2-text-tertiary)', lineHeight: 'var(--leading-normal)' }}>
            {installedCount} installed
            {!tierLimit.unlimited && ` · ${tier1Installed} of ${tierLimit.limit} auto-install slots used`}
          </p>
        </div>
        <button
          onClick={() => onRefresh()}
          onMouseEnter={() => setRefreshHover(true)}
          onMouseLeave={() => setRefreshHover(false)}
          title="Refresh"
          style={{ background: 'none', border: '1px solid var(--v2-border)', borderRadius: 'var(--radius-md)', padding: '8px 14px', cursor: 'pointer', color: refreshHover ? 'var(--v2-primary)' : 'var(--v2-text-tertiary)', display: 'flex', alignItems: 'center', gap: '6px', fontSize: 'var(--text-sm)', minHeight: 'var(--target-md)', transition: 'all 0.15s ease' }}
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Tier limit warnings */}
      {!tierLimit.unlimited && tierLimit.current > tierLimit.limit && (
        <div style={{ padding: '14px 20px', backgroundColor: 'var(--v2-error-bg)', border: '1px solid #fca5a5', borderRadius: 'var(--radius-lg)', display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '24px' }}>
          <AlertCircle size={18} strokeWidth={1.5} color="var(--v2-error)" style={{ flexShrink: 0, marginTop: '1px' }} />
          <span style={{ fontSize: 'var(--text-base)', color: 'var(--v2-error)', lineHeight: 'var(--leading-normal)' }}>
            <strong>Over limit:</strong> You have {tierLimit.current} driver{tierLimit.current !== 1 ? 's' : ''} installed but your plan only allows {tierLimit.limit}. Please uninstall {tierLimit.current - tierLimit.limit} driver{(tierLimit.current - tierLimit.limit) !== 1 ? 's' : ''} to come back into compliance.
          </span>
        </div>
      )}
      {!tierLimit.unlimited && tierLimit.current === tierLimit.limit && !canInstallTier1 && (
        <div style={{ padding: '14px 20px', backgroundColor: 'var(--v2-error-bg)', border: '1px solid #fca5a5', borderRadius: 'var(--radius-lg)', display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
          <AlertCircle size={18} strokeWidth={1.5} color="var(--v2-error)" style={{ flexShrink: 0 }} />
          <span style={{ fontSize: 'var(--text-base)', color: 'var(--v2-error)', lineHeight: 'var(--leading-normal)' }}>
            Auto-install driver limit reached ({tierLimit.current}/{tierLimit.limit}). Upgrade your plan or uninstall a driver to install more.
          </span>
        </div>
      )}

      {/* Tier 1 */}
      {tier1Drivers.length > 0 && (
        <div style={{ marginBottom: '32px' }}>
          <div style={{ marginBottom: '12px' }}>
            <h3 style={{ margin: 0, fontSize: 'var(--text-lg)', fontWeight: 600, color: 'var(--v2-text)', lineHeight: 'var(--leading-tight)' }}>Auto-Install Drivers</h3>
            <p style={{ margin: '4px 0 0', fontSize: 'var(--text-base)', color: 'var(--v2-text-tertiary)', lineHeight: 'var(--leading-normal)' }}>
              One-click install — these drivers are downloaded and configured automatically.
            </p>
          </div>
          <DriverTable
            drivers={tier1Drivers} tier={1} tierLimit={tierLimit} canTier2={canTier2}
            activeJobDriverKey={activeJobDriverKey} activeJobStatus={activeJobStatus} actionLoading={actionLoading}
            onInstall={(key) => handleInstall(key)}
            onOpenTier2Modal={setTier2ModalDriver}
            onOpenUninstallModal={setUninstallModalDriver}
          />
        </div>
      )}

      {/* Tier 2 */}
      {tier2Drivers.length > 0 && (
        <div>
          <div style={{ marginBottom: '12px' }}>
            <h3 style={{ margin: 0, fontSize: 'var(--text-lg)', fontWeight: 600, color: 'var(--v2-text)', lineHeight: 'var(--leading-tight)' }}>Customer Upload Drivers</h3>
            <p style={{ margin: '4px 0 0', fontSize: 'var(--text-base)', color: 'var(--v2-text-tertiary)', lineHeight: 'var(--leading-relaxed)' }}>
              These drivers require a license agreement or login with the vendor. Download from the vendor site, then provide the download URL to install.
            </p>
          </div>
          <DriverTable
            drivers={tier2Drivers} tier={2} tierLimit={tierLimit} canTier2={canTier2}
            activeJobDriverKey={activeJobDriverKey} activeJobStatus={activeJobStatus} actionLoading={actionLoading}
            onInstall={(key) => handleInstall(key)}
            onOpenTier2Modal={setTier2ModalDriver}
            onOpenUninstallModal={setUninstallModalDriver}
          />
        </div>
      )}

{/* Tier 2 Install Modal */}
      {tier2ModalDriver && (
        <Tier2InstallModal
          driver={tier2ModalDriver}
          onInstall={(key, url) => handleInstall(key, url)}
          onClose={() => { setTier2ModalDriver(null); setInstallError(null); }}
          busy={actionLoading === tier2ModalDriver.driver_key}
          errorMessage={installError}
        />
      )}

      {/* Uninstall Confirm Modal */}
      {uninstallModalDriver && (
        <UninstallModal
          driver={uninstallModalDriver}
          onUninstall={handleUninstall}
          onClose={() => { setUninstallModalDriver(null); setUninstallError(null); }}
          busy={actionLoading === uninstallModalDriver.driver_key}
          errorMessage={uninstallError}
        />
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default OdbcDriversCore;
